import React, { useEffect, useState } from 'react';
import { api, CustomField, FieldKind } from './api';
import { Select } from './Select';
import { OptionEditor, KIND_LABEL, fldLink, fldChip } from './Settings';

/** Admin: DEFINE the org's member/profile fields + the option pools members choose from
 *  (department, skill tags, project assignments…). Opened from More → Admin → Member fields.
 *  Members fill in their own values from their Profile settings — that half lives elsewhere. */
export function FieldAdminModal({ workspaceId, onClose }: { workspaceId: string; onClose: () => void }) {
  const [fields, setFields] = useState<CustomField[] | null>(null);
  const [newLabel, setNewLabel] = useState('');
  const [newKind, setNewKind] = useState<FieldKind>('text');
  const [newOptions, setNewOptions] = useState<string[]>([]);
  const [editingOpts, setEditingOpts] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.listFields(workspaceId).then((fs) => { if (alive) setFields(fs); }).catch(() => { if (alive) setFields([]); });
    return () => { alive = false; };
  }, [workspaceId]);

  const addField = async () => {
    const label = newLabel.trim();
    if (!label || busy) return;
    if (newKind !== 'text' && newOptions.length === 0) { window.alert('Add at least one option for a choice field.'); return; }
    setBusy(true);
    try {
      const f = await api.createField(workspaceId, { label, kind: newKind, options: newKind !== 'text' ? newOptions : undefined });
      setFields((cur) => [...(cur || []), f]);
      setNewLabel(''); setNewKind('text'); setNewOptions([]);
    } catch (e: any) { window.alert(e?.message || 'Could not add the field'); }
    finally { setBusy(false); }
  };
  const removeField = async (id: string) => {
    if (!window.confirm('Remove this field for the whole workspace? Everyone’s values for it go too.')) return;
    try { await api.deleteField(workspaceId, id); setFields((cur) => (cur || []).filter((f) => f.id !== id)); } catch { /* ignore */ }
  };
  const saveOptions = async (id: string, options: string[]) => {
    if (options.length === 0) { window.alert('A choice field needs at least one option.'); return; }
    setFields((cur) => (cur || []).map((f) => (f.id === id ? { ...f, options } : f)));  // optimistic
    try { await api.updateField(workspaceId, id, { options }); } catch (e: any) { window.alert(e?.message || 'Could not update options.'); }
  };

  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal" onClick={(e) => e.stopPropagation()} style={{ width: 'min(560px, 94vw)', maxHeight: '88vh', overflowY: 'auto' }}>
        <div className="fx-login-title">Member fields &amp; tags</div>
        <p style={{ color: 'var(--fx-muted)', fontSize: 13, lineHeight: 1.5, margin: '4px 0 14px' }}>
          Define the fields your members fill in on their profile — and, for choice fields, the pool
          of options they pick from. Great for department, skills, project assignments, or desk.
          Members set their own values in <b>Profile → Your details</b>.
        </p>

        {fields === null ? (
          <div style={{ color: 'var(--fx-muted)', fontSize: 14 }}>Loading…</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {fields.map((f) => (
              <div key={f.id} style={{ border: '1px solid var(--fx-hair)', borderRadius: 10, padding: '10px 12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 650, fontSize: 14, color: 'var(--fx-text)' }}>{f.label}</span>
                  <span style={{ fontSize: 11, color: 'var(--fx-muted)', border: '1px solid var(--fx-hair)', borderRadius: 20, padding: '1px 7px' }}>{KIND_LABEL[f.kind]}</span>
                  <span style={{ flex: 1 }} />
                  {f.kind !== 'text' && (
                    <button type="button" style={fldLink} onClick={() => setEditingOpts((e) => (e === f.id ? null : f.id))}>
                      {editingOpts === f.id ? 'Done' : 'Edit options'}
                    </button>
                  )}
                  <button type="button" style={{ ...fldLink, color: '#e56a6a' }} onClick={() => removeField(f.id)}>Remove</button>
                </div>
                {f.kind !== 'text' && editingOpts === f.id
                  ? <OptionEditor options={f.options} onChange={(o) => saveOptions(f.id, o)} />
                  : f.kind !== 'text' && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                      {f.options.map((o) => <span key={o} style={{ ...fldChip, cursor: 'default' }}>{o}</span>)}
                    </div>
                  )}
              </div>
            ))}
            {fields.length === 0 && <div style={{ color: 'var(--fx-muted)', fontSize: 13.5 }}>No fields yet — add one below.</div>}

            <div style={{ border: '1px dashed var(--fx-hair)', borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ fontSize: 12.5, fontWeight: 650, color: 'var(--fx-muted)', marginBottom: 8 }}>Add a field</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <input className="fx-text-input" style={{ width: 200 }} value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)} placeholder="Field name (e.g. Department)" />
                <Select value={newKind} onChange={(k) => { setNewKind(k as FieldKind); if (k === 'text') setNewOptions([]); }}
                  style={{ width: 160 }}
                  options={[{ value: 'text', label: 'Text' }, { value: 'select', label: 'Single choice' }, { value: 'tags', label: 'Multiple tags' }]} />
              </div>
              {newKind !== 'text' && (
                <>
                  <div style={{ fontSize: 12, color: 'var(--fx-muted)', marginTop: 10 }}>Options members can choose from:</div>
                  <OptionEditor options={newOptions} onChange={setNewOptions} />
                </>
              )}
              <div style={{ marginTop: 10 }}>
                <button type="button" className="fx-sendbtn" onClick={addField} disabled={busy || !newLabel.trim() || (newKind !== 'text' && newOptions.length === 0)}>
                  {busy ? 'Adding…' : 'Add field'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" className="fx-btn" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}
